/**
 * roomNoise — "이 방은 측정에 쓸 수 없을 만큼 시끄럽다"는 판정. 순수 함수다.
 *
 * 근거는 음성 게이트의 바닥이다(`libs/shared/src/scoring/voice-gate.ts`). 게이트는
 * 300ms 창의 중앙값으로 이 방의 조용함을 재는데, 그 값이 `-40dBFS`보다 시끄러우면
 * 창을 버리고 다시 잰다. 그래서 **바닥이 오래도록 안 정해진다 = 조용한 창이 한 번도
 * 없었다**가 성립한다.
 *
 * 다만 바닥은 **사용자 목소리로도** 오염된다 — 시작하자마자 계속 소리를 내면 조용한
 * 창이 안 생긴다. 그래서 "엔진이 음을 한 번도 못 들었을 때"만 방을 탓한다. 목소리를
 * 듣고 있는 동안은 절대 이 화면을 띄우지 않는다: 잘 부르고 있는 사람의 측정을
 * 끊어 놓고 방 탓을 하는 것이 이 판정이 저지를 수 있는 가장 나쁜 실수다.
 *
 * React·시간 의존 없음 — vitest 대상.
 */

/**
 * 이 시간이 지나도 바닥이 안 잡히면 방을 의심한다(초).
 *
 * 게이트의 캘리브레이션 창이 300ms라 정상적인 방은 첫 창에서 바로 잡힌다. 4초면
 * 열 번 넘게 다시 잰 뒤라 "조용한 순간이 한 번도 없었다"가 우연이 아니다.
 */
export const ROOM_NOISE_AFTER_SEC = 4;

export interface RoomNoiseInput {
  /** 게이트가 이 방의 바닥을 정했나(`LiveSample.floorLocked`). */
  floorLocked: boolean;
  /** 이번 테이크에서 엔진이 실제로 들은 발성 길이(초). */
  voicedSec: number;
  /** 테이크가 시작된 뒤 흐른 벽시계 시간. */
  elapsedSec: number;
  /**
   * 이 테이크에 반주가 같이 나가나(노래 진단).
   *
   * 그러면 **방을 탓하지 않는다.** 판정의 전제는 "조용한 창이 한 번도 없었다면 방이
   * 시끄럽다"인데, 스피커로 반주를 틀어 놓은 테이크에서는 조용한 창이 없는 것이 정상이라
   * 전제가 통째로 무너진다. 실제로 iOS에서 반주가 들리게 되자마자 노래 진단이 시작 몇 초
   * 만에 소음 화면으로 끊겼다(2026-09-07).
   */
  backingTrack?: boolean;
}

/**
 * 전체화면 소음 안내(시안 `884:9858`)를 띄울지.
 *
 * 셋이 모두 참이어야 한다: 바닥이 안 잡혔고, 볼 만큼 기다렸고, 그동안 목소리는
 * 한 번도 안 들렸다. 반주가 나가는 테이크는 이 판정 자체를 건너뛴다(`backingTrack`).
 */
export const isRoomTooNoisy = ({
  floorLocked,
  voicedSec,
  elapsedSec,
  backingTrack = false,
}: RoomNoiseInput): boolean =>
  !backingTrack &&
  !floorLocked &&
  elapsedSec >= ROOM_NOISE_AFTER_SEC &&
  voicedSec === 0;
